import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import { NodeElement } from '../../../utils/NodeElement';
import svgIcon from './assets/icons/shortcode.svg';
import './assets/styles.css';

export default class Shortcodes extends Plugin {

    init() {
        if (window.editorSettings.shortcodes.length === 0) {
            return;
        }

        const editor = this.editor;
        editor.ui.componentFactory.add('shortcodes', (locale) => {
            this._initTranslations(locale);

            const buttonView = new ButtonView(locale);

            buttonView.set({
                label: 'Shortcodes',
                withText: false,
                icon: svgIcon,
                tooltip: true,
            });

            buttonView.on('execute', () => {
                this._openPopup(locale);
            });

            return buttonView;
        });
    }

    _openPopup(locale) {
        let wrapperNode = NodeElement.createFromName('div')
            .addClass('ck-editor-shortcodes-popup')
            .attach(document.body);

        let contentNode = NodeElement.createFromName('div')
            .addClass('ck-editor-shortcodes-popup__content')
            .attach(wrapperNode);

        NodeElement.createFromName('div')
            .addClass('ck-editor-shortcodes-popup__close')
            .setInnerText('╳')
            .setProperty('type', 'button')
            .addEventListener('click', (event) => {
                event.preventDefault();
                wrapperNode.detach();

                return false;
            })
            .attach(contentNode);

        NodeElement.createFromName('h1')
            .setInnerText(locale.t('Shortcodes'))
            .attach(contentNode);

        let listNode = NodeElement.createFromName('div')
            .addClass('ck-editor-shortcodes-popup__list')
            .attach(contentNode);

        window.editorSettings.shortcodes.forEach(shortcode => {
            let itemNode = NodeElement.createFromName('div')
                .addClass('ck-editor-shortcodes-popup__item')
                .attach(listNode);

                NodeElement.createFromName('h2')
                    .addClass('ck-editor-shortcodes-popup__item-heading')
                    .setInnerText(shortcode.name)
                    .attach(itemNode);

                NodeElement.createFromName('p')
                    .addClass('ck-editor-shortcodes-popup__item-description')
                    .setInnerText(
                        this._translateFromList(locale, shortcode.configuration.translations)
                    )
                    .attach(itemNode);

                NodeElement.createFromName('p')
                    .addClass('ck-editor-shortcodes-popup__item-type')
                    .setInnerText(
                        locale.t('Type') + ': ' + (shortcode.configuration['pairwise'] ? locale.t('Paired') : locale.t('Unpaired'))
                    )
                    .attach(itemNode);

            let attributesString = '';

            if (Object.keys(shortcode.configuration.attributes).length) {
                NodeElement.createFromName('p')
                    .addClass('ck-editor-shortcodes-popup__item-attributes')
                    .setInnerText(
                        locale.t('Attributes') + ':'
                    )
                    .attach(itemNode);

                let itemAttributesListNode = NodeElement.createFromName('ul')
                    .addClass('ck-editor-shortcodes-popup__item-attributes-list')
                    .attach(itemNode);

                for (const [attributeName, attributeConfiguration] of Object.entries(shortcode.configuration.attributes)) {
                    let defaultValue = '';
                    if (attributeConfiguration.flags.default) {
                        defaultValue = attributeConfiguration.flags.default;
                    } else if (attributeConfiguration.flags.required) {
                        defaultValue = '*';
                    }

                    attributesString += ' ' + attributeName + "='" + defaultValue + "'";

                    let attributeLabel = attributeName;
                    if (attributeConfiguration.flags.required) {
                        attributeLabel += ' <strong>(*)</strong>';
                    }
                    attributeLabel += ' <sup>' + attributeConfiguration.type + '</sup>';
                    let attributeTranslation = this._translateFromList(locale, attributeConfiguration.translations);
                    if (attributeTranslation) {
                        attributeLabel += '<i>' + attributeTranslation + '</i>';
                        // attributeLabel += '<i> – ' + attributeTranslation + '</i>';
                    }

                    NodeElement.createFromName('li')
                        .setInnerHtml(attributeLabel)
                        .attach(itemAttributesListNode);
                }
            }

            itemNode.appendChild(
                NodeElement.createFromName('button')
                    .setProperty('type', 'button')
                    .addClass('ck-editor-shortcodes-popup__item-button')
                    .setInnerText(locale.t('Use'))
                    .addEventListener('click', (event) => {
                        event.preventDefault();

                        if (shortcode.configuration['pairwise']) {
                            this._editorInsertText('[' + shortcode.name + attributesString + ']', '[/' + shortcode.name + ']');
                            wrapperNode.detach();

                            return false;
                        }

                        this._editorInsertText('[' + shortcode.name + attributesString + ']');
                        wrapperNode.detach();

                        return false;
                    })
            );
        });
    }

    _translateFromList(locale, translations) {
        if (typeof translations[locale.uiLanguage] !== 'undefined') {
            return translations[locale.uiLanguage];
        }

        if (typeof translations['en'] !== 'undefined') {
            return translations['en'];
        }

        return '';
    }

    /**
     * @private
     */
    _initTranslations() {
        this._addTranslation('cs', {
            'Shortcodes': 'Shortkódy',
            'Unpaired': 'Nepárový',
            'Paired': 'Párový',
            'Attributes': 'Atributy',
            'Use': 'Použít',
        });
        this._addTranslation('sk', {
            'Shortcodes': 'Shortkódy',
            'Unpaired': 'Nepárový',
            'Paired': 'Párový',
            'Attributes': 'Atribúty',
            'Use': 'Použiť',
        });
        this._addTranslation('en', {
            'Shortcodes': 'Shortcodes',
            'Unpaired': 'Unpaired',
            'Paired': 'Paired',
            'Attributes': 'Attributes',
            'Use': 'Use',
        });
    }

    /**
     * @param language
     * @param translations
     * @param getPluralForm
     * @private
     */
    _addTranslation( language, translations, getPluralForm ) {
        if ( !window.CKEDITOR_TRANSLATIONS[ language ] ) {
            window.CKEDITOR_TRANSLATIONS[ language ] = {};
        }

        const languageTranslations = window.CKEDITOR_TRANSLATIONS[ language ];

        languageTranslations.dictionary = languageTranslations.dictionary || {};
        languageTranslations.getPluralForm = getPluralForm || languageTranslations.getPluralForm;

        Object.assign( languageTranslations.dictionary, translations );
    }

    /**
     * @param textStart
     * @param textEnd
     * @private
     */
    _editorInsertText(textStart, textEnd) {
        this.editor.model.change(writer => {
            let firstPosition = this.editor.model.document.selection.getFirstPosition();
            let lastPosition = this.editor.model.document.selection.getLastPosition();

            if (textEnd && firstPosition.isEqual(lastPosition)) {
                textStart += textEnd;
                textEnd = null;
            }

            if (textEnd) {
                writer.insertText(textEnd, lastPosition);
            }

            if (textStart) {
                writer.insertText(textStart, firstPosition);
            }
        });
    }
}
